import type { TFullUnit, TSimpleUnit } from '../../../shared/domain/units/types.no-deps'
import { compact } from '../../../shared/lib/functional/array.no-deps'
import { concat } from '../../../shared/lib/functional/asyncGenerator.no-deps'
import i18next from 'i18next'
import type { Identifiable } from '../../../shared/lib/typeHelpers.no-deps'

export type Unit = KnownUnit | UnknownUnit
export type KnownUnit = { kind: 'KnownUnit' } & TFullUnit
export type UnknownUnit = { kind: 'UnknownUnit'; id: number }

export function isKnownUnit(unit: Unit): unit is KnownUnit {
    return unit.kind === 'KnownUnit'
}

export function isUnknownUnit(unit: Unit): unit is UnknownUnit {
    return unit.kind === 'UnknownUnit'
}

export function knownUnit(unit: TFullUnit): KnownUnit {
    return { kind: 'KnownUnit', ...unit }
}

export function unknownUnit(unitId: number): UnknownUnit {
    return { kind: 'UnknownUnit', id: unitId }
}

export function unitName(unit: Unit) {
    if (unit.kind === 'KnownUnit') return unit.name

    return i18next.t('appcore:unit.unrecognised')
}

export function unitImage(unit: Unit | Identifiable<TSimpleUnit>) {
    if ('kind' in unit && unit.kind === 'UnknownUnit') return null
    return unit.data?.image
}

export type FetchUnitsByTypes = (unitTypes: number[]) => Promise<KnownUnit[]>
export type FetchUnitsByIds = (unitIds: number[]) => Promise<KnownUnit[]>

export function createCachedFetchByIds(fetchByIds: FetchUnitsByIds): FetchUnitsByIds {
    const cache = new Map<number, KnownUnit>()

    return async (unitIds) => {
        // Find units not it cache
        const newUnits = await fetchByIds(unitIds.filter((unitId) => !cache.has(unitId)))

        // Add them to the cache
        for (const unit of newUnits) cache.set(unit.id, unit)

        // Then return all the units from the cache
        return unitIds.map((unitId) => cache.get(unitId))
    }
}

/**
 * Search for relatives to a unit that have unit type
 * First try the parents and then try the children as there are probably less parents
 * This generally assumes that well formed unit data should have units of each generation having the same unit type
 */
export async function findRelativesWithUnitType(unit: KnownUnit, unitTypeId: number, fetchByIds: FetchUnitsByIds) {
    const generator = concat(
        unitGenerations([unit], 'parents', fetchByIds),
        unitGenerations([unit], 'children', fetchByIds)
    )

    for await (const generation of generator) {
        const unitsWithType = generation.filter((u) => u.type === unitTypeId && !u.isDeleted)
        if (unitsWithType.length === 0) continue
        return unitsWithType
    }
}

export async function* unitGenerations(
    units: KnownUnit[],
    direction: 'parents' | 'children',
    fetchByIds: FetchUnitsByIds
): AsyncGenerator<KnownUnit[]> {
    while (true) {
        const parents = await fetchUnitGeneration(units, direction, fetchByIds)
        if (parents.length === 0) break
        yield parents
        units = parents
    }
}

export async function fetchUnitGeneration(
    units: KnownUnit[],
    direction: 'parents' | 'children',
    fetchByIds: FetchUnitsByIds
): Promise<KnownUnit[]> {
    const ids = new Set<number>()

    for (const unit of units) {
        for (const id of unit[direction]) {
            ids.add(id)
        }
    }

    if (ids.size === 0) return []

    return fetchByIds(Array.from(ids)).then(compact)
}
